import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { paperApi } from '../services/api';
import { formatINR, formatPercent } from '../utils/formatters';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Wallet, ArrowsClockwise, TrendUp, TrendDown, Trash, Coins } from '@phosphor-icons/react';

const OPTION_UNDERLYINGS = ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'RELIANCE', 'TCS', 'HDFCBANK'];

export default function PaperTrading() {
  const [account, setAccount] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [instrument, setInstrument] = useState('EQUITY');
  const [symbol, setSymbol] = useState('RELIANCE');
  const [quantity, setQuantity] = useState(10);
  const [expiry, setExpiry] = useState('07-Aug-2026');
  const [strike, setStrike] = useState(24500);
  const [optionType, setOptionType] = useState('CE');
  const [placing, setPlacing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, t] = await Promise.all([paperApi.account(), paperApi.trades()]);
      setAccount(a.data);
      setTrades(t.data.trades || []);
    } catch (e) {
      console.error('paper account load failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 30000);  // refresh live prices every 30s
    return () => clearInterval(timer);
  }, [load]);

  const placeOrder = useCallback(async (side) => {
    setPlacing(true);
    try {
      const payload = { symbol: symbol.toUpperCase(), side, quantity: Number(quantity), instrument };
      if (instrument === 'OPTION') {
        Object.assign(payload, { expiry, strike: Number(strike), option_type: optionType });
      }
      const { data } = await paperApi.order(payload);
      const t = data.trade;
      const label = t.instrument === 'OPTION' ? `${t.symbol} ${t.strike} ${t.option_type}` : t.symbol;
      toast.success(`${side} ${t.quantity} ${label} @ ₹${t.price}`);
      await load();
    } catch (e) {
      console.error('order failed', e);
      toast.error(e.response?.data?.detail || 'Order failed');
    } finally {
      setPlacing(false);
    }
  }, [symbol, quantity, instrument, expiry, strike, optionType, load]);

  const resetAccount = useCallback(async () => {
    if (!window.confirm('Reset paper account? All holdings + trades will be cleared and cash reset to ₹10 lakh.')) return;
    try {
      await paperApi.reset();
      toast.success('Account reset');
      await load();
    } catch (e) {
      console.error('reset failed', e);
      toast.error('Reset failed');
    }
  }, [load]);

  if (loading) {
    return <Layout><div className="p-8 text-center text-[#94A3B8]">Loading…</div></Layout>;
  }
  if (!account) {
    return <Layout><div className="p-8 text-center text-[#EF4444]">Failed to load account</div></Layout>;
  }

  return (
    <Layout>
      <div className="w-full max-w-[1920px] mx-auto px-6 py-6" data-testid="paper-trading-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Wallet size={32} weight="duotone" className="text-[#10B981]" />
              Paper Trading
            </h1>
            <p className="text-sm text-[#94A3B8] mt-1">
              Virtual portfolio at live NSE prices • Starts at ₹10,00,000
            </p>
          </div>
          <Button variant="outline" onClick={resetAccount}
            className="border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10"
            data-testid="paper-reset-btn">
            <Trash size={16} className="mr-1.5" /> Reset Account
          </Button>
        </div>

        <StatCards a={account} />
        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6 mt-6">
          <OrderForm
            instrument={instrument} setInstrument={setInstrument}
            symbol={symbol} setSymbol={setSymbol}
            quantity={quantity} setQuantity={setQuantity}
            expiry={expiry} setExpiry={setExpiry}
            strike={strike} setStrike={setStrike}
            optionType={optionType} setOptionType={setOptionType}
            placing={placing}
            onBuy={() => placeOrder('BUY')} onSell={() => placeOrder('SELL')}
          />
          <HoldingsTable holdings={account.holdings} />
        </div>
        <TradesLog trades={trades} />
      </div>
    </Layout>
  );
}

function StatCards({ a }) {
  const pnlColor = a.total_pnl >= 0 ? '#10B981' : '#EF4444';
  const lifetimeColor = a.lifetime_return_percent >= 0 ? '#10B981' : '#EF4444';
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="paper-stat-cards">
      <StatCard label="Cash" value={formatINR(a.cash_balance)} testid="paper-cash" />
      <StatCard label="Invested" value={formatINR(a.invested)} testid="paper-invested" />
      <StatCard label="Current Value" value={formatINR(a.current_value)} testid="paper-current-value" />
      <StatCard label="Net Worth" value={formatINR(a.net_worth)} subtext={`${formatPercent(a.lifetime_return_percent)} lifetime`}
        color={lifetimeColor} testid="paper-net-worth" />
      <StatCard label="Open P&L" value={`${a.total_pnl >= 0 ? '+' : ''}${formatINR(a.total_pnl)}`}
        subtext={formatPercent(a.total_pnl_percent)} color={pnlColor} testid="paper-open-pnl" />
    </div>
  );
}

function StatCard({ label, value, subtext, color = '#FFFFFF', testid }) {
  return (
    <div className="p-3 border border-white/10 bg-white/[0.02]" data-testid={testid}>
      <div className="text-[10px] text-[#64748B] uppercase tracking-wide">{label}</div>
      <div className="text-lg font-mono font-bold mt-1" style={{ color }}>{value}</div>
      {subtext && <div className="text-[10px] mt-0.5" style={{ color }}>{subtext}</div>}
    </div>
  );
}

function OrderForm({
  instrument, setInstrument, symbol, setSymbol, quantity, setQuantity,
  expiry, setExpiry, strike, setStrike, optionType, setOptionType,
  placing, onBuy, onSell,
}) {
  const isOption = instrument === 'OPTION';
  return (
    <div className="border border-white/10 p-4 space-y-3 h-fit" data-testid="paper-order-form">
      <h3 className="text-sm font-bold text-white flex items-center gap-2">
        <ArrowsClockwise size={16} /> Place Order
      </h3>

      {/* Instrument toggle */}
      <div className="flex gap-1 border border-white/10 p-1">
        {['EQUITY', 'OPTION'].map((k) => (
          <button
            key={k}
            onClick={() => setInstrument(k)}
            className={`flex-1 py-1.5 text-xs font-mono uppercase transition-colors ${
              instrument === k ? 'bg-[#2563EB] text-white' : 'text-[#94A3B8] hover:text-white'
            }`}
            data-testid={`instrument-${k}`}
          >
            {k === 'OPTION' ? <><Coins size={12} className="inline mr-1" /> F&O</> : 'Equity'}
          </button>
        ))}
      </div>

      <div>
        <label className="block text-[10px] uppercase text-[#94A3B8] mb-1.5">
          {isOption ? 'Underlying' : 'Symbol'}
        </label>
        {isOption ? (
          <select value={symbol} onChange={(e) => setSymbol(e.target.value)}
            className="w-full bg-[#0A0B0E] border border-white/20 text-white p-2 text-sm"
            data-testid="paper-symbol-select">
            {OPTION_UNDERLYINGS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        ) : (
          <Input value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            className="bg-[#0A0B0E] border-white/20 text-white" placeholder="RELIANCE"
            data-testid="paper-symbol-input" />
        )}
      </div>

      {isOption && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] uppercase text-[#94A3B8] mb-1.5">Strike</label>
              <Input type="number" value={strike} onChange={(e) => setStrike(Number(e.target.value))}
                className="bg-[#0A0B0E] border-white/20 text-white" data-testid="paper-strike-input" />
            </div>
            <div>
              <label className="block text-[10px] uppercase text-[#94A3B8] mb-1.5">Type</label>
              <div className="flex gap-1 border border-white/10 p-1">
                {['CE', 'PE'].map((t) => (
                  <button key={t} onClick={() => setOptionType(t)}
                    className={`flex-1 py-1 text-xs font-mono ${
                      optionType === t ? (t === 'CE' ? 'bg-[#10B981] text-black' : 'bg-[#EF4444] text-white') : 'text-[#94A3B8]'
                    }`}
                    data-testid={`paper-optype-${t}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase text-[#94A3B8] mb-1.5">Expiry</label>
            <Input value={expiry} onChange={(e) => setExpiry(e.target.value)}
              placeholder="07-Aug-2026"
              className="bg-[#0A0B0E] border-white/20 text-white" data-testid="paper-expiry-input" />
          </div>
        </>
      )}

      <div>
        <label className="block text-[10px] uppercase text-[#94A3B8] mb-1.5">
          Quantity{isOption ? ' (lots × lot size)' : ''}
        </label>
        <Input type="number" min={1} value={quantity} onChange={(e) => setQuantity(Number(e.target.value))}
          className="bg-[#0A0B0E] border-white/20 text-white" data-testid="paper-qty-input" />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Button onClick={onBuy} disabled={placing}
          className="bg-[#10B981] hover:bg-green-600 text-black font-semibold" data-testid="paper-buy-btn">
          <TrendUp size={16} className="mr-1" /> BUY
        </Button>
        <Button onClick={onSell} disabled={placing}
          className="bg-[#EF4444] hover:bg-red-600 text-white font-semibold" data-testid="paper-sell-btn">
          <TrendDown size={16} className="mr-1" /> SELL
        </Button>
      </div>
      <p className="text-[10px] text-[#64748B]">
        {isOption ? 'F&O orders execute at synthesised chain LTP (mocked pricing).' : 'Executes at live BSE LTP.'}
      </p>
    </div>
  );
}

function HoldingsTable({ holdings }) {
  if (holdings.length === 0) {
    return (
      <div className="border border-dashed border-white/10 p-8 text-center text-[#64748B] text-sm">
        No holdings yet — place your first order to get started.
      </div>
    );
  }
  return (
    <div className="border border-white/10 overflow-hidden">
      <table className="w-full text-xs" data-testid="paper-holdings-table">
        <thead className="bg-white/5 text-[#94A3B8]">
          <tr>
            <th className="text-left py-2 px-3">Symbol</th>
            <th className="text-right py-2 px-3">Qty</th>
            <th className="text-right py-2 px-3">Avg ₹</th>
            <th className="text-right py-2 px-3">LTP ₹</th>
            <th className="text-right py-2 px-3">Invested</th>
            <th className="text-right py-2 px-3">Value</th>
            <th className="text-right py-2 px-3">P&L</th>
          </tr>
        </thead>
        <tbody>
          {holdings.map((h) => (
            <tr key={h.symbol} className="border-t border-white/5"
              data-testid={`paper-holding-${h.symbol}`}>
              <td className="py-2 px-3 font-mono text-white">{h.symbol}</td>
              <td className="py-2 px-3 text-right font-mono text-white">{h.quantity}</td>
              <td className="py-2 px-3 text-right font-mono text-[#94A3B8]">{h.avg_price}</td>
              <td className="py-2 px-3 text-right font-mono text-white">{h.current_price}</td>
              <td className="py-2 px-3 text-right font-mono text-white">{formatINR(h.invested)}</td>
              <td className="py-2 px-3 text-right font-mono text-white">{formatINR(h.current_value)}</td>
              <td className={`py-2 px-3 text-right font-mono ${h.pnl >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                {h.pnl >= 0 ? '+' : ''}{formatINR(h.pnl)} ({formatPercent(h.pnl_percent)})
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TradesLog({ trades }) {
  if (trades.length === 0) return null;
  return (
    <div className="mt-6">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">Trade History ({trades.length})</p>
      <div className="border border-white/10 overflow-x-auto max-h-72 overflow-y-auto">
        <table className="w-full text-xs" data-testid="paper-trades-log">
          <thead className="bg-white/5 text-[#94A3B8] sticky top-0">
            <tr>
              <th className="text-left py-2 px-3">Time</th>
              <th className="text-left py-2 px-3">Symbol</th>
              <th className="text-center py-2 px-3">Side</th>
              <th className="text-right py-2 px-3">Qty</th>
              <th className="text-right py-2 px-3">Price</th>
              <th className="text-right py-2 px-3">Value</th>
            </tr>
          </thead>
          <tbody>
            {trades.map((t) => (
              <tr key={t.id} className="border-t border-white/5">
                <td className="py-1.5 px-3 text-[#94A3B8] font-mono">
                  {new Date(t.executed_at).toLocaleString('en-IN', { hour12: false })}
                </td>
                <td className="py-1.5 px-3 font-mono text-white">{t.symbol}</td>
                <td className={`py-1.5 px-3 text-center font-mono font-bold ${t.side === 'BUY' ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                  {t.side}
                </td>
                <td className="py-1.5 px-3 text-right font-mono text-white">{t.quantity}</td>
                <td className="py-1.5 px-3 text-right font-mono text-white">{formatINR(t.price)}</td>
                <td className="py-1.5 px-3 text-right font-mono text-white">{formatINR(t.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
